import { useLazyQuery, useQuery } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import PromotionSummary from '../../components/PromotionSummary'
import { BOOKING_DETAILS_QUERY } from '../../graphql/bookingDetail'
import { ACCOMMODATION_IMAGES_QUERY } from '../../graphql/upload'
import { BookingDetail } from '../../types/bookingDetails'
import { ImageObject } from '../../types/image'
import AmountDetails from './components/AmountDetails'
import BillingSummary from './components/BillingSummary'
import BookerDetails from './components/BookerDetails'
import LastChanger from './components/LastChanger'
import ReserveDetails from './components/ReserveDetails'
import RoomDetails from './components/RoomDetails'
import SourceInfo from './components/SourceInfo'
import Header from './header'
import useStyles from './styles/index.styles'

const BookingDetailsResults = () => {
  const classes = useStyles()
  const theme = useTheme()
  const params = useParams<{ reservationNumber: string }>()
  const [bookingDetails, setBookingDetails] = useState<BookingDetail>()

  const [getAccommodationImages, { data: accoommodationImages }] =
    useLazyQuery<{
      accommodationImages: ImageObject[]
    }>(ACCOMMODATION_IMAGES_QUERY, {
      fetchPolicy: 'network-only',
      onError: (err) => {
        toast.error(err.message)
      }
    })

  const { data: bookingDetailData, loading: loadingForBookingDetails } =
    useQuery<{
      bookingDetail: BookingDetail
    }>(BOOKING_DETAILS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        reservationNumber: params.reservationNumber
      },
      onCompleted: (res) => {
        setBookingDetails(res.bookingDetail)
        getAccommodationImages({
          variables: {
            propertyCode: res.bookingDetail.basicDetails.propertyCode
          }
        })
      },
      onError: (error) => {
        toast.error(error.message)
      }
    })

  return (
    <div className={classes.root}>
      <Header
        data={bookingDetailData?.bookingDetail}
        bookingDetail={bookingDetails}
      />
      <div className={classes.basicDetails}>
        <div className={classes.left} data-testid="booking-details-left-column">
          <ReserveDetails
            data={bookingDetails?.basicDetails}
            accommodationImages={accoommodationImages?.accommodationImages}
          />
          <BookerDetails bookerInfo={bookingDetails?.bookerInfo} />
          <div className={classes.lastChangerSourceInfo}>
            <LastChanger data={bookingDetails?.lastChanger} />
            <SourceInfo data={bookingDetails?.sourceInfo} />
          </div>
        </div>
        <div
          className={classes.right}
          data-testid="booking-details-right-column">
          <PromotionSummary
            application={bookingDetails?.promotionApplication}
          />
          <BillingSummary
            billingInfo={bookingDetailData?.bookingDetail.billingSummary}
            promotionApplication={bookingDetails?.promotionApplication}
            rooms={bookingDetails?.rooms}
          />
        </div>
      </div>
      {bookingDetails?.rooms.map((room, i) => (
        <div key={i}>
          <RoomDetails
            room={room}
            currency={bookingDetails?.basicDetails.currency}
            index={i + 1}
          />
        </div>
      ))}
      <AmountDetails
        curruncy={bookingDetails?.basicDetails.currency}
        roomsPriceDetails={bookingDetails?.roomsPriceDetails}
      />
      <AdotelSkeleton
        open={loadingForBookingDetails}
        numberOfCircles={4}
        circlesColor={theme.palette.primary.main}
      />
    </div>
  )
}

export default BookingDetailsResults
